import classNames from 'classnames';
import { Briefcase, Task } from 'components/Icons';
import { TicMetaIcon } from 'components/TicMetaIcon';
import React from 'react';
import type { TicMeta } from 'types/tic';
import { EMPTY_OBJECT } from '../../constants/defaults';
import styles from './ProjectBadge.module.scss';

export type BadgeIconValues = boolean | 'task';

/**
 * Props for ProjectBadge component.
 */
interface Props extends React.HTMLAttributes<HTMLDivElement> {
  /** Project color (hex with or without #) */
  color: string;

  /** Badge size variant */
  size?: 'xsmall' | 'small' | 'medium' | 'large';

  /**
   * Icon display mode.
   * - `true`: Shows Briefcase icon inside colored box
   * - `'task'`: Renders as Task SVG icon (no box, just colored icon)
   * - `false`/undefined: Shows colored box only
   * @remarks If tweaking, check HourEditor's ProjectSelect for size alignment
   */
  icon?: BadgeIconValues;

  /** Makes badge circular */
  rounded?: boolean;

  /**
   * TIC (Timely Integration Cloud) metadata.
   * When present with valid `icon`, shows small tool icon overlay in top-right.
   * Overlay is hidden for 'xsmall' size to avoid visual clutter.
   * @see TicMeta
   */
  ticMeta?: TicMeta;
}

/** Map badge sizes to TicMetaIcon sizes */
const ticIconSizeMap = {
  xsmall: 'small', // won't be shown, but needed for type safety
  small: 'small',
  medium: 'medium',
  large: 'medium',
} as const;

const ProjectBadge = (props: Props) => {
  const {
    children,
    className,
    rounded,
    color,
    icon,
    size = 'medium',
    style,
    ticMeta,
    ...rest
  } = props;
  const badgeClass = classNames(styles.container, styles[`size-${size}`], {
    [styles.rounded]: rounded,
    [styles.asIconWithoutBox]: icon === 'task',
  });
  const containerStyle = { ...style, backgroundColor: color };

  const showTicOverlay = !!ticMeta?.icon;
  const ticIconSize = ticIconSizeMap[size];

  // Task icon variant (renders as colored SVG without box)
  if (icon === 'task') {
    const taskIcon = (
      <Task
        className={showTicOverlay ? badgeClass : classNames(badgeClass, className)}
        style={{ ...style, color }}
        {...(showTicOverlay ? EMPTY_OBJECT : (rest as React.SVGAttributes<SVGSVGElement>))}
      />
    );

    return showTicOverlay ? (
      <TicMetaIcon.Overlay className={className} ticMeta={ticMeta} size={ticIconSize} {...rest}>
        {taskIcon}
      </TicMetaIcon.Overlay>
    ) : (
      taskIcon
    );
  }

  // Standard badge (colored box with optional briefcase icon)
  const badge = (
    <div
      className={showTicOverlay ? badgeClass : classNames(badgeClass, className)}
      style={containerStyle}
      {...(showTicOverlay ? EMPTY_OBJECT : rest)}
    >
      {!!icon && <Briefcase />}
      {children}
    </div>
  );

  return showTicOverlay ? (
    <TicMetaIcon.Overlay className={className} ticMeta={ticMeta} size={ticIconSize} {...rest}>
      {badge}
    </TicMetaIcon.Overlay>
  ) : (
    badge
  );
};

export default ProjectBadge;
